import { useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { services } from '@/data/services';
import { ZoomableImage } from '@/components/common/ImageLightbox';
import { MotionReveal } from '@/components/common/MotionReveal';
import { SectionTitle } from '@/components/common/SectionTitle';

const easeOutQuint = [0.22, 1, 0.36, 1] as const;

export function FeaturedServicesSection() {
  const [activeId, setActiveId] = useState(services[0].id);
  const activeService =
    services.find(service => service.id === activeId) ?? services[0];
  const reduceMotion = useReducedMotion();

  return (
    <section
      id="usluge-preview"
      className="scroll-mt-20 py-22 sm:py-28 lg:py-36"
    >
      <div className="site-container">
        <div className="grid gap-6 lg:grid-cols-[0.72fr_0.58fr] lg:items-end lg:justify-between lg:gap-24">
          <SectionTitle
            eyebrow="Usluge"
            title="Podrška prilagođena fazi u kojoj se posao nalazi."
          />
          <p className="max-w-lg text-base leading-7 text-text-secondary lg:pb-1">
            Možemo da pripremimo plan, vodimo realizaciju ili se uključimo u deo
            procesa koji trenutno usporava tim.
          </p>
        </div>

        <div className="mt-11 grid gap-9 lg:mt-16 lg:grid-cols-[0.72fr_1.28fr] lg:items-start lg:gap-18">
          <MotionReveal className="order-2 lg:order-1">
            <div className="border-t border-border">
              {services.map(service => {
                const active = service.id === activeId;
                return (
                  <button
                    key={service.id}
                    type="button"
                    className="group flex w-full cursor-pointer items-center justify-between gap-5 border-b border-border py-5 text-left sm:py-6"
                    onMouseEnter={() => setActiveId(service.id)}
                    onFocus={() => setActiveId(service.id)}
                    onClick={() => setActiveId(service.id)}
                    aria-pressed={active}
                  >
                    <span
                      className={`text-lg font-semibold tracking-[-0.025em] transition-colors sm:text-xl ${active ? 'text-primary' : 'text-text-primary group-hover:text-primary'}`}
                    >
                      {service.title}
                    </span>
                    <span className="flex w-10 justify-end" aria-hidden="true">
                      {active && (
                        <motion.span
                          layoutId="active-service-indicator"
                          className="h-px w-8 bg-primary"
                          transition={{ duration: 0.22, ease: easeOutQuint }}
                        />
                      )}
                    </span>
                  </button>
                );
              })}
            </div>

            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={activeService.id}
                className="mt-6"
                initial={reduceMotion ? false : { opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={reduceMotion ? undefined : { opacity: 0, y: -5 }}
                transition={{ duration: 0.22, ease: easeOutQuint }}
              >
                <p className="max-w-lg text-sm leading-6 text-text-secondary">
                  {activeService.shortDescription}
                </p>
                <a
                  className="animated-link group mt-4 inline-flex items-center gap-2 text-sm font-semibold text-text-primary transition-colors hover:text-primary"
                  href={`/usluge#${activeService.id}`}
                >
                  Detalji usluge
                  <ArrowRight
                    aria-hidden="true"
                    className="size-4 transition-transform duration-200 group-hover:translate-x-1"
                  />
                </a>
              </motion.div>
            </AnimatePresence>
          </MotionReveal>

          <div className="order-1 overflow-hidden lg:order-2">
            <AnimatePresence mode="wait" initial={false}>
              <motion.figure
                key={activeService.id}
                initial={
                  reduceMotion
                    ? false
                    : { opacity: 0, clipPath: 'inset(0 0 0 12%)' }
                }
                animate={{ opacity: 1, clipPath: 'inset(0 0 0 0%)' }}
                exit={reduceMotion ? undefined : { opacity: 0 }}
                transition={{ duration: 0.38, ease: easeOutQuint }}
              >
                <ZoomableImage
                  className="aspect-[4/3.05] w-full rounded-[var(--radius-md)] object-cover lg:aspect-[16/11]"
                  src={activeService.image}
                  alt={activeService.imageAlt}
                  loading="lazy"
                />
                <figcaption className="mt-3 flex justify-between gap-4 text-xs text-text-muted">
                  <span>{activeService.eyebrow}</span>
                  <span>
                    Usluga{' '}
                    {services.findIndex(service => service.id === activeId) + 1}{' '}
                    od {services.length}
                  </span>
                </figcaption>
              </motion.figure>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
